/**
 * Coordinate scaling utilities for VizOverlay.
 *
 * Handles transformation between coordinate spaces:
 * - Detect-space: 2000px width (Sentinel detection coordinates)
 * - Original-space: Full-frame camera resolution (7008px for a9II)
 * - Display-space: Browser viewport (variable)
 *
 * All coordinates use XYXY format (x1, y1, x2, y2) per CLAUDE.md.
 */

import { getOrientedSourceDims } from './sourceDims';

/**
 * Create a coordinate scaler for transforming coordinates between spaces.
 *
 * @param {number} sourceWidth - Width of source coordinate space
 * @param {number} sourceHeight - Height of source coordinate space
 * @param {number} displayWidth - Width of display area in pixels
 * @param {number} displayHeight - Height of display area in pixels
 * @returns {Object} Scaler with transform methods
 */
export function createCoordinateScaler(
  sourceWidth,
  sourceHeight,
  displayWidth,
  displayHeight
) {
  const scaleX = displayWidth / sourceWidth;
  const scaleY = displayHeight / sourceHeight;

  return {
    /**
     * Scale a single X coordinate.
     * @param {number} sourceX
     * @returns {number}
     */
    x: (sourceX) => sourceX * scaleX,

    /**
     * Scale a single Y coordinate.
     * @param {number} sourceY
     * @returns {number}
     */
    y: (sourceY) => sourceY * scaleY,

    /**
     * Scale a bounding box from source to display space.
     * @param {Object} sourceBox - {x1, y1, x2, y2}
     * @returns {Object} Scaled box in display space
     */
    box: (sourceBox) => ({
      x1: sourceBox.x1 * scaleX,
      y1: sourceBox.y1 * scaleY,
      x2: sourceBox.x2 * scaleX,
      y2: sourceBox.y2 * scaleY,
    }),

    /**
     * Scale a point from source to display space.
     * @param {Object} sourcePoint - {x, y}
     * @returns {Object} Scaled point
     */
    point: (sourcePoint) => ({
      x: sourcePoint.x * scaleX,
      y: sourcePoint.y * scaleY,
    }),

    /**
     * Convert box to CSS percentage values for responsive positioning.
     * @param {Object} sourceBox - {x1, y1, x2, y2}
     * @returns {Object} CSS style object with left, top, width, height as percentages
     */
    boxToPercent: (sourceBox) => ({
      left: `${(sourceBox.x1 / sourceWidth) * 100}%`,
      top: `${(sourceBox.y1 / sourceHeight) * 100}%`,
      width: `${((sourceBox.x2 - sourceBox.x1) / sourceWidth) * 100}%`,
      height: `${((sourceBox.y2 - sourceBox.y1) / sourceHeight) * 100}%`,
    }),

    /**
     * Get scale factors for external use.
     * @returns {Object} {scaleX, scaleY}
     */
    getScaleFactors: () => ({ scaleX, scaleY }),
  };
}

/**
 * Determine coordinate space of crop box based on method.
 * Per cropCalculations.js:
 * - "user_recrop" = full-frame space (original dimensions)
 * - All other methods = detect-space (2000px width)
 *
 * @param {string|undefined} cropMethod
 * @returns {'detect'|'original'}
 */
export function getCropCoordinateSpace(cropMethod) {
  return cropMethod === 'user_recrop' ? 'original' : 'detect';
}

/**
 * Extract viz-relevant metadata from the full metaDoc structure.
 * The raw metaDoc has pipeline nesting; this extracts what VizOverlay needs.
 *
 * @param {Object} metaDoc - Full metadata document from API
 * @returns {Object|null} Extracted viz metadata or null if not available
 */
export function extractVizMetadata(metaDoc) {
  if (!metaDoc) return null;

  // Sentinel metadata may be at top level or nested in pipeline.sentinel
  const sentinelData = metaDoc.pipeline?.sentinel || metaDoc;

  return {
    image_id: metaDoc.image_id || metaDoc.id,
    focus_info: sentinelData.focus_info || null,
    roll_info: sentinelData.roll_info || null,
    detections: sentinelData.detections || [],
    primary_subject: sentinelData.primary_subject || null,
    crop: sentinelData.crop || metaDoc.crop || null,
    quality: sentinelData.quality || null,
    image_dims: sentinelData.image_dims || metaDoc.image_dims || null,
    // Additional metadata for coordinate space determination
    originalDims: getOrientedSourceDims(metaDoc),
  };
}
